/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, ReactElement, useEffect, useMemo, useReducer, useState } from 'react';
import { decodeToken } from 'react-jwt';

import axios from 'src/utils/axios';
import {RoleEnum, UserDto} from '../../requests/auth/dto';
import {baseAdminURL, baseEmployeeURL, basePortalURL, baseRetailerURL} from '../../routes';
import {isValidToken, setRefreshToken, setSession} from './Jwt';
import {refreshTokenRequest} from '../../requests/auth';
import {getPortalType} from './utils';
import {omit} from 'lodash';

const API_URL = process.env.REACT_APP_API_URL || 'https://product.velomatch.io';

export interface InitialStateType {
    isAuthenticated: boolean;
    isInitialized?: boolean;
    user?: UserDto | null;
}

export interface AuthContextType extends InitialStateType {
    [k: string]: unknown;

    platform: 'JWT';
    method: 'jwt';
    accessStatus: AccessStatus | null;
    isMfaEnabled: boolean | null;
    logoutForAllPortals: () => void,
    signin: (email: string, password: string, pageToLogin: string) => Promise<void>;
    refreshToken: () => Promise<void>;
    logout: (portalType: RoleEnum) => Promise<void>;
    resetPassword: (hash: string, password: string, resetPassword: string) => Promise<void>;
    setUpDeityMode: (tenantId: string, role: RoleEnum, employeeId?: string, retailerId?: string) => Promise<void>;
    exitDeityMode: (portalType: RoleEnum) => Promise<void>;
    generateQRCode: () => Promise<{ qrCode: string, secret: string }>;
    enableTwoFactor: (code: string, secret: string, password?: string) => Promise<void>;
    generateBackupCodes: () => Promise<{ backupCodes: string[] }>;
    verifyTwoFactor: (code: string, rememberDevice?: boolean) => Promise<void>;
    usingBackupCode: (code: string) => Promise<void>;
    regenerateBackupCodes: (password: string, code?: string) => Promise<{ backupCodes: string[] }>;
    getCurrentBackupCodes: (code: string, password: string) => Promise<{ backupCodes: string[], countOfExist: number }>;
    copiedMFACodes: () => Promise<void>;
    isEmployer: boolean;
    isRetailer: boolean;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    isNotAdmin: boolean;
    isEmployee: boolean;
}

export enum AccessStatus {
    VERIFY_MFA = 'VERIFY_MFA', // Indicates the user must verify MFA
    MFA_REQUIRED = 'MFA_REQUIRED', // Indicates the user needs to enter MFA code for each session
    BACKUP_CODE_SAVE = 'BACKUP_CODE_SAVE', // Indicates the user needs to enter MFA code for each session
    FULL_ACCESS = 'FULL_ACCESS', // Indicates MFA has been verified or disabled
}

export interface UserFromAccessToken {
    email?: string;
    firstName?: string;
    lastName?: string;
    tenantId?: number;
    roles?: string[];
    accessStatus?: AccessStatus;
}

const initialState: InitialStateType = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
};

const getUserFromAccessToken = (accessToken: string): UserDto => {
    const userFromToken: UserFromAccessToken = decodeToken<UserFromAccessToken>(accessToken) || {};
    const user = omit(userFromToken) as unknown as UserDto;

    user.roles = [userFromToken.roles![0] as RoleEnum];
    user.tenantId = userFromToken.tenantId;

    return user;
};

const handlers: any = {
    INITIALIZE: (state: InitialStateType, action: any) => {
        const {isAuthenticated, user} = action.payload;

        return {
            ...state,
            isAuthenticated,
            isInitialized: true,
            user,
            accessStatus: user?.accessStatus,
            isMfaEnabled: user?.isMfaEnabled,
        };
    },
    LOGIN: (state: InitialStateType, action: any) => {
        const { user } = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            user,
            accessStatus: user?.accessStatus,
            isMfaEnabled: user?.isMfaEnabled,
        };
    },
    LOGOUT: (state: InitialStateType) => ({
        ...state,
        isAuthenticated: false,
        user: null,
        accessStatus: null,
        isMfaEnabled: null,
    }),
    REGISTER: (state: InitialStateType, action: any) => {
        const {user} = action.payload;

        return {
            ...state,
            isAuthenticated: true,
            user,
            accessStatus: user?.accessStatus,
            isMfaEnabled: user?.isMfaEnabled,
        };
    },
};

const reducer = (state: InitialStateType, action: any) =>
    handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextType>({
    ...initialState,
    method: 'jwt',
    platform: 'JWT',
    accessStatus: null,
    isMfaEnabled: null,
    signin: () => Promise.resolve(),
    logoutForAllPortals: () => undefined,
    refreshToken: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    resetPassword: () => Promise.resolve(),
    setUpDeityMode: (tenantId: string, role: RoleEnum, employeeId?: string, retailerId?: string) => Promise.resolve(),
    exitDeityMode: (portalType: RoleEnum) => Promise.resolve(),
    generateQRCode: () => Promise.resolve({qrCode: "", secret: ""}),
    generateBackupCodes: () => Promise.resolve({ backupCodes: [""] }),
    enableTwoFactor: (code: string, secret: string, password?: string) => Promise.resolve(),
    verifyTwoFactor: (code: string, rememberDevice?: boolean) => Promise.resolve(),
    usingBackupCode: (code: string) => Promise.resolve(),
    copiedMFACodes: () => Promise.resolve(),
    regenerateBackupCodes: (password: string, code?: string) => Promise.resolve({ backupCodes: [""] }),
    getCurrentBackupCodes: (code: string, password: string) => Promise.resolve({ backupCodes: [], countOfExist: 0}),
    isEmployer: false,
    isNotAdmin: false,
    isAdmin: false,
    isRetailer: false,
    isSuperAdmin: false,
    isEmployee: false,
});

function AuthProvider({children}: { children: ReactElement }) {
    const [initialization, setInitialization] = useState(true);

    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        const portalType = getPortalType(window.location.pathname);
        const initialize = async () => {
            try {
                const token = window.localStorage.getItem(`${portalType?.toLowerCase()}-token`);
                const refreshJwtToken = window.localStorage.getItem(
                    `${portalType?.toLowerCase()}-refresh-token`,
                );


                if (token && refreshJwtToken && isValidToken(refreshJwtToken)) {
                    if (token && !isValidToken(token)) {
                        setRefreshToken(refreshJwtToken, portalType);
                        try {
                            await refreshToken();
                        } catch (e) {
                            await logoutForAllPortals();
                        }
                    } else {
                        setSession(token, portalType);
                        setRefreshToken(refreshJwtToken, portalType);

                        const user = decodeToken<Partial<UserDto>>(token);
                        if (!user) {
                            throw new Error('Invalid token.');
                        }

                        dispatch({
                            type: 'INITIALIZE',
                            payload: {
                                isAuthenticated: true,
                                user: {...(state.user ?? {}), ...user},
                            },
                        });
                    }
                } else {
                    await logout(portalType);
                }
            } catch (err) {
                dispatch({
                    type: 'INITIALIZE',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                });
            } finally {
                setInitialization(false);
            }
        };

        initialize();
    }, []);

    const signin = async (email: string, password: string, pageToLogin: string) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/email/login', {
            email,
            password,
            pageToLogin,
        }, {
            withCredentials: true
        });

        const {accessToken, refreshToken} = response.data;
        const user = getUserFromAccessToken(accessToken);

        setSession(accessToken, pageToLogin);
        setRefreshToken(refreshToken, pageToLogin);

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const getCurrentBackupCodes = async (code: string, password: string) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/2fa/get-current-backup-codes', {
            code,
            password
        });
        const { backupCodes, countOfExist } = response.data;

        return {
            backupCodes,
            countOfExist
        }
    };

    const refreshToken = async () => {
        const response = await refreshTokenRequest();
        const { accessToken } = response;

        const user = getUserFromAccessToken(accessToken);

        if (user.roles && user.roles[0]) {
            setSession(accessToken, user.roles[0]);
        }

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const resetPassword = async (hash: string, password: string, confirmPassword: string) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/reset/password', {
            hash,
            password,
            confirmPassword,
        });

        const {accessToken, refreshToken} = response.data;
        const user = getUserFromAccessToken(accessToken);

        setSession(accessToken, user.roles![0]);
        setRefreshToken(refreshToken, user.roles![0]);

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const generateBackupCodes = async () => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/2fa/generate-backup-codes');
        const { backupCodes } = response.data;

        return {
            backupCodes,
        }
    };

    const regenerateBackupCodes = async (password: string, code: string) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/2fa/regenerate-backup-codes', {
            password,
            code
        });
        const { backupCodes } = response.data;

        return {
            backupCodes,
        }
    };

    const usingBackupCode = async (code: string) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/2fa/use-backup-code', {
            code
        });

        const { accessToken, refreshToken } = response.data;
        const user = getUserFromAccessToken(accessToken);

        setSession(accessToken, user.roles![0]);
        setRefreshToken(refreshToken, user.roles![0]);

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const copiedMFACodes = async (code: string) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/2fa/copied-backup-codes', {
            code
        });

        const { accessToken, refreshToken } = response.data;
        const user = getUserFromAccessToken(accessToken);

        setSession(accessToken, user.roles![0]);
        setRefreshToken(refreshToken, user.roles![0]);

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const generateQRCode = async () => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/2fa/generate');
        const {qrCode, secret} = response.data;

        return {
            qrCode,
            secret
        }
    };

    const enableTwoFactor = async (code: string, secret: string, password: string) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/2fa/enable', {
            code,
            secret,
            password
        });

        const {accessToken, refreshToken} = response.data;
        const user = getUserFromAccessToken(accessToken);

        setSession(accessToken, user.roles![0]);
        setRefreshToken(refreshToken, user.roles![0]);

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const verifyTwoFactor = async (code: string, rememberDevice?: boolean) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/2fa/verify', {
            code,
            rememberDevice
        }, {
            withCredentials: true
        });

        const {accessToken, refreshToken} = response.data;
        const user = getUserFromAccessToken(accessToken);

        setSession(accessToken, user.roles![0]);
        setRefreshToken(refreshToken, user.roles![0]);

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const logout = async (portalType?: RoleEnum) => {
        if (portalType) {
            setSession(null, portalType);
            setRefreshToken(null, portalType);
        }
        dispatch({type: 'LOGOUT'});
    };

    const logoutForAllPortals = async () => {
        for (const portalType in RoleEnum) {
            setSession(null, portalType);
            setRefreshToken(null, portalType);
        }

        dispatch({type: 'LOGOUT'});
    };

    const exitDeityMode = async (portalType: RoleEnum) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/exit-deity-mode');

        const {accessToken, refreshToken} = response.data;

        const user = getUserFromAccessToken(accessToken);

        setSession(accessToken, RoleEnum.ADMIN);
        setRefreshToken(refreshToken, RoleEnum.ADMIN);

        if (portalType) {
            setSession(null, portalType);
            setRefreshToken(null, portalType);
        }

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });

        window.location.replace(`${window.location.origin}${baseAdminURL}/dashboard`);
    };

    const setUpDeityMode = async (tenantId: string, role: RoleEnum, employeeId?: string, retailerId?: string) => {
        const response = await axios.post(API_URL + '/gogeta-api/v1/auth/deity-mode', {
            tenantId,
            role,
            employeeId,
            retailerId
        });

        const {accessToken, refreshToken} = response.data;

        const user = getUserFromAccessToken(accessToken);

        setSession(accessToken, role);
        setRefreshToken(refreshToken, role);

        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });

        if (role === RoleEnum.EMPLOYEE) {
            window.location.replace(`${window.location.origin}${baseEmployeeURL}/dashboard`);
        } else if (role === RoleEnum.EMPLOYER) {
            window.location.replace(`${window.location.origin}${basePortalURL}/dashboard`);
        } else {
            window.location.replace(`${window.location.origin}${baseRetailerURL}/vouchers`);
        }
    };

    const {isAdmin, isSuperAdmin, isNotAdmin, isEmployer, isRetailer, isEmployee} = useMemo(() => {
        const permissions = {
            isEmployer: false,
            isAdmin: false,
            isSuperAdmin: false,
            isNotAdmin: false,
            isRetailer: false,
            isEmployee: false,
        };

        if (!state.user) {
            return permissions;
        }

        if (state.user.roles.length === 1) {
            permissions.isAdmin = state.user.roles.includes(RoleEnum.ADMIN);
        }

        if (state.user.roles.length !== 1) {
            permissions.isSuperAdmin = state.user.roles.includes(RoleEnum.ADMIN);
        }

        permissions.isNotAdmin = !state.user.roles.includes(RoleEnum.ADMIN);
        permissions.isEmployer = state.user.roles.includes(RoleEnum.EMPLOYER);
        permissions.isRetailer = state.user.roles.includes(RoleEnum.RETAILER);
        permissions.isEmployee = state.user.roles.includes(RoleEnum.EMPLOYEE);

        return permissions;
    }, [state.user]);

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'jwt',
                signin,
                logout,
                resetPassword,
                refreshToken,
                isEmployer,
                isEmployee,
                isRetailer,
                isAdmin,
                isSuperAdmin,
                isNotAdmin,
                exitDeityMode,
                setUpDeityMode,
                generateQRCode,
                enableTwoFactor,
                verifyTwoFactor,
                generateBackupCodes,
                usingBackupCode,
                regenerateBackupCodes,
                copiedMFACodes,
                getCurrentBackupCodes,
                logoutForAllPortals
            }}
        >
            {initialization ? null : children}
        </AuthContext.Provider>
    );
}

export {AuthContext, AuthProvider};
